import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useOnReconnect } from "@/hooks/useOnReconnect";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { nivelLabelColor } from "@/lib/constants";
import {
  FolderKanban, GraduationCap, Users, Dumbbell, ClipboardList,
  CheckCircle2, ClipboardCheck, ArrowRight, ShieldAlert,
} from "lucide-react";

const CARDS = [
  { key: "cursos_ativos", label: "Cursos ativos", icon: FolderKanban, color: "text-sky-400", route: "/cursos" },
  { key: "turmas_ativas", label: "Turmas em andamento", icon: GraduationCap, color: "text-purple-400", route: "/turmas" },
  { key: "total_alunos", label: "Alunos cadastrados", icon: Users, color: "text-amber-400", route: "/alunos" },
  { key: "total_oficinas", label: "Oficinas", icon: Dumbbell, color: "text-emerald-400", route: "/oficinas" },
  { key: "avaliacoes_pendentes", label: "Avaliações pendentes", icon: ClipboardList, color: "text-orange-400", route: "/avaliacoes?status=em_andamento" },
  { key: "avaliacoes_concluidas", label: "Avaliações concluídas", icon: CheckCircle2, color: "text-teal-400", route: "/avaliacoes?status=concluida" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [instrucaoPI, setInstrucaoPI] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data));
  useEffect(() => {
    load();
    api.get("/cursos?tipo=instrucao").then((r) => {
      const list = r.data || [];
      const pi = list.find((c) => c.banco_criterios === "primeiro_interventor")
        || list.find((c) => (c.nome || "").toUpperCase() === "PRIMEIRO INTERVENTOR EM CENÁRIOS DE CRISE")
        || list[0];
      setInstrucaoPI(pi || null);
    }).catch(() => setInstrucaoPI(null));
  }, []);
  useOnReconnect(load);

  return (
    <div>
      <PageHeader title="Início" subtitle="Visão geral do sistema de avaliação" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <button data-testid="home-iniciar-avaliacao" onClick={() => navigate("/avaliar")}
          className="flex items-center gap-3 p-5 rounded-2xl border border-primary/40 bg-primary/10 hover:bg-primary/15 transition-all text-left active:scale-[0.99]">
          <div className="p-3 rounded-xl bg-primary/20 shrink-0"><ClipboardCheck className="h-6 w-6 text-primary" /></div>
          <div className="min-w-0"><div className="font-heading text-lg font-bold leading-tight">INICIAR AVALIAÇÃO</div><div className="text-xs text-muted-foreground">Criar ou selecionar uma nova atividade</div></div>
          <ArrowRight className="h-5 w-5 ml-auto text-primary shrink-0" />
        </button>
        <button data-testid="home-continuar-avaliacao" onClick={() => navigate("/avaliar?modo=continuar")}
          className="flex items-center gap-3 p-5 rounded-2xl border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/15 transition-all text-left active:scale-[0.99]">
          <div className="p-3 rounded-xl bg-sky-500/20 shrink-0"><ClipboardList className="h-6 w-6 text-sky-400" /></div>
          <div className="min-w-0"><div className="font-heading text-lg font-bold leading-tight">CONTINUAR AVALIAÇÃO</div><div className="text-xs text-muted-foreground">Retomar uma avaliação em andamento</div></div>
          <ArrowRight className="h-5 w-5 ml-auto text-sky-400 shrink-0" />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <Card key={c.key} data-testid={`stat-${c.key}`} onClick={() => navigate(c.route)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(c.route); }}
            className="p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all active:scale-[0.98]">
            <c.icon className={`h-6 w-6 ${c.color}`} />
            <div className="mt-3 font-heading text-4xl font-extrabold">
              {stats ? stats[c.key] : "–"}
            </div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mt-1">{c.label}</div>
          </Card>
        ))}
      </div>

      {instrucaoPI && (
        <button data-testid="home-instrucao-card" onClick={() => navigate(`/cursos/${instrucaoPI.id}`)}
          className="mt-6 w-full flex items-center gap-4 p-5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 transition-all text-left active:scale-[0.99]">
          <div className="p-3 rounded-xl bg-amber-500/20 shrink-0"><ShieldAlert className="h-6 w-6 text-amber-400" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold">Instrução</div>
            <div className="font-heading text-lg font-bold leading-tight break-words">{instrucaoPI.nome}</div>
            <div className="text-xs text-muted-foreground">Acesso direto à instrução oficial</div>
          </div>
          <ArrowRight className="h-5 w-5 text-amber-400 shrink-0" />
        </button>
      )}

      <div className="mt-8">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-4">Atividade recente</h2>
        <Card className="bg-card border-border divide-y divide-border">
          {stats?.recentes?.length ? (
            stats.recentes.map((r) => (
              <button
                key={r.id}
                data-testid={`recent-${r.id}`}
                onClick={() => navigate(`/avaliacoes/${r.id}`)}
                className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.aluno_nome}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.oficina_nome}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {r.status === "concluida" ? (
                    <Badge className={nivelLabelColor(r.nivel_geral)}>{r.nivel_geral || "Concluída"}</Badge>
                  ) : (
                    <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">Em andamento</Badge>
                  )}
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))
          ) : (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              Nenhuma avaliação registrada ainda.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
